import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { dashboardApi as D, VIEW_LABELS } from "@/lib/dashboard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";
import ExecutiveView from "@/pages/dashboard/ExecutiveView";
import ProductionView from "@/pages/dashboard/ProductionView";
import InventoryView from "@/pages/dashboard/InventoryView";
import DispatchView from "@/pages/dashboard/DispatchView";
import FinanceView from "@/pages/dashboard/FinanceView";

const FETCHERS = { executive: D.executive, production: D.production, inventory: D.inventory, dispatch: D.dispatch, finance: D.finance };
const VIEWS = { executive: ExecutiveView, production: ProductionView, inventory: InventoryView, dispatch: DispatchView, finance: FinanceView };

export default function Dashboard() {
  const { user } = useAuth();
  const [views, setViews] = useState([]);
  const [tab, setTab] = useState(null);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const tabRef = useRef(tab);
  tabRef.current = tab;

  useEffect(() => {
    D.meta().then((m) => { setViews(m.views || []); setTab((m.views || ["executive"])[0]); }).catch(() => { setViews(["executive"]); setTab("executive"); });
  }, []);

  const loadTab = useCallback(async (view, silent = false) => {
    if (!view) return;
    if (silent) setRefreshing(true); else setLoading(true);
    try { const d = await FETCHERS[view](); setData((prev) => ({ ...prev, [view]: d })); setUpdatedAt(new Date()); }
    catch (e) { if (e?.response?.status !== 403) console.error("Dashboard view load failed", e); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { if (tab) loadTab(tab); }, [tab, loadTab]);

  // Auto-refresh every 30s (silent).
  useEffect(() => {
    const id = setInterval(() => { if (tabRef.current) loadTab(tabRef.current, true); }, 30000);
    return () => clearInterval(id);
  }, [loadTab]);

  const Active = tab ? VIEWS[tab] : null;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Welcome, {user?.name?.split(" ")[0]}</h2>
          <p className="mt-1 text-sm text-slate-500">Live overview across the factory. {updatedAt && <span data-testid="dash-updated">Updated {updatedAt.toLocaleTimeString()}</span>}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => loadTab(tab, true)} disabled={refreshing} data-testid="dash-refresh-btn">
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />Refresh
        </Button>
      </div>

      {!tab ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div> : (
        <Tabs value={tab} onValueChange={setTab}>
          {views.length > 1 && (
            <TabsList data-testid="dashboard-tabs">
              {views.map((v) => <TabsTrigger key={v} value={v} data-testid={`dash-tab-${v}`}>{VIEW_LABELS[v]}</TabsTrigger>)}
            </TabsList>
          )}
          {views.map((v) => {
            const View = VIEWS[v];
            return (
              <TabsContent key={v} value={v} className="mt-4">
                {loading && !data[v] ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div> : <View data={data[v]} />}
              </TabsContent>
            );
          })}
        </Tabs>
      )}
    </div>
  );
}
